import React, { useState, useEffect, useRef } from "react";
import { TreeInstance } from "@headless-tree/core";
import { Datum } from "./datastructure";

export interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  itemId: string | null;
}

export interface ContextMenuAction {
  label: string;
  action: string;
  disabled?: boolean;
  separator?: boolean;
}

export interface ContextMenuProps {
  contextMenu: ContextMenuState;
  actions: ContextMenuAction[];
  onMenuAction: (action: string, itemId: string) => void;
}

/**
 * Renders a positioned context menu for a set of actions. Deliberately knows
 * nothing about the file tree — search results build their own rows and share
 * this component, which is what keeps the two surfaces' menus from drifting.
 */
export const ContextMenu: React.FC<ContextMenuProps> = ({
  contextMenu,
  actions,
  onMenuAction,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjusted, setAdjusted] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!contextMenu.visible) {
      setAdjusted(false);
      return;
    }
    const padding = 8;
    let adjustedX = contextMenu.x;
    let adjustedY = contextMenu.y;

    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      if (adjustedY + rect.height > window.innerHeight - padding) {
        adjustedY = window.innerHeight - rect.height - padding;
      }
      if (adjustedX + rect.width > window.innerWidth - padding) {
        adjustedX = window.innerWidth - rect.width - padding;
      }
    }

    setPosition({ x: adjustedX, y: adjustedY });
    setAdjusted(true);
  }, [contextMenu.visible, contextMenu.x, contextMenu.y]);

  if (!contextMenu.visible || !contextMenu.itemId) return null;

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border border-gray-300 rounded-md shadow-lg py-1 z-[1600]"
      style={{
        left: adjusted ? position.x : contextMenu.x,
        top: adjusted ? position.y : contextMenu.y,
        minWidth: "160px",
        visibility: adjusted ? "visible" : "hidden",
      }}
      onMouseDown={(e) => e.stopPropagation()} // Prevent closing when clicking on menu
    >
      {actions.map((menuAction, index) => {
        if (menuAction.separator) {
          return <hr key={`separator-${index}`} className="my-1" />;
        }

        return (
          <div
            key={menuAction.action}
            className={`px-3 py-2 text-sm ${
              menuAction.disabled
                ? "text-gray-400 cursor-not-allowed"
                : "hover:bg-gray-100 cursor-pointer"
            }`}
            onClick={() =>
              !menuAction.disabled &&
              onMenuAction(menuAction.action, contextMenu.itemId!)
            }
          >
            {menuAction.label}
          </div>
        );
      })}
    </div>
  );
};

export interface UseFiletreeContextMenuOptions {
  tree: TreeInstance<Datum>;
  getMenuActions?: (itemId: string, item: any) => ContextMenuAction[];
  onMenuAction?: (action: string, itemId: string) => void;
}

export const useFiletreeContextMenu = ({
  tree,
  getMenuActions,
  onMenuAction,
}: UseFiletreeContextMenuOptions) => {
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    itemId: null,
  });

  // Close context menu when clicking outside or pressing escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenu.visible) {
        setContextMenu((prev) => ({ ...prev, visible: false }));
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && contextMenu.visible) {
        setContextMenu((prev) => ({ ...prev, visible: false }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [contextMenu.visible]);

  const handleContextMenu = (event: React.MouseEvent, itemId: string) => {
    event.preventDefault();
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      itemId,
    });
  };

  const handleMenuAction = (action: string, itemId: string) => {
    setContextMenu((prev) => ({ ...prev, visible: false }));
    onMenuAction?.(action, itemId);
  };

  // Get menu actions for current context menu item
  const menuActions: ContextMenuAction[] =
    contextMenu.itemId && getMenuActions
      ? getMenuActions(
          contextMenu.itemId,
          tree.getItemInstance(contextMenu.itemId)
        )
      : [];

  return {
    contextMenu,
    handleContextMenu,
    handleMenuAction,
    menuActions,
    ContextMenuComponent: (
      <ContextMenu
        contextMenu={contextMenu}
        actions={menuActions}
        onMenuAction={handleMenuAction}
      />
    ),
  };
};
